import React, { useState } from "react";
import GoogleButton from "./GoogleButton";
import { appleSignInAvailable, signInWithApple } from "../lib/appleSignIn";
import {
  FanUser,
  fanEmailStart,
  fanSignInApple,
  fanSignInGoogle,
} from "../lib/fanAccount";
import "./FanClaimButtons.css";
import { t } from "../i18n";

// The three fan sign-in doors (locked r6): Apple → Google → email, in that
// order. Rendered inside the sign-in sheet, the My Profile editor's CLAIM
// YOUR SPOT section, and the /fan/login re-send screen. "Continue with
// email" expands to an inline field; sending swaps the whole block to the
// "Check your email ✉️" state. No passwords anywhere, ever.

const isAppleCancel = (err: any) =>
  err?.error === "popup_closed_by_user" ||
  /cancel/i.test(err?.message || "");

const EMAIL_RE = /.+@.+\..+/;

const FanClaimButtons: React.FC<{
  /** Editor sizing (11px pad / 14px font) vs sheet sizing (13px / 15px). */
  compact?: boolean;
  /** "email" opens with the field already expanded (/fan/login re-send). */
  initialMode?: "buttons" | "email";
  onSignedIn?: (fan: FanUser) => void;
}> = ({ compact, initialMode = "buttons", onSignedIn }) => {
  const [mode, setMode] = useState<"buttons" | "email" | "sent">(initialMode);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sentNote, setSentNote] = useState("");

  const run = async (flow: () => Promise<FanUser>) => {
    setBusy(true);
    setError("");
    try {
      const fan = await flow();
      onSignedIn?.(fan);
    } catch (err: any) {
      if (!isAppleCancel(err)) {
        console.error("Fan sign-in failed:", err);
        setError(t("fanClaimButtons.couldnTSignYou"));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleApple = () =>
    run(async () => {
      const result = await signInWithApple();
      if (!result?.idToken) throw new Error("no apple token");
      return fanSignInApple(result.idToken, result.user?.name);
    });

  const handleGoogle = (credential: string) => run(() => fanSignInGoogle(credential));

  const sendLink = async () => {
    const addr = email.trim();
    if (!EMAIL_RE.test(addr)) return;
    setBusy(true);
    setError("");
    try {
      await fanEmailStart(addr);
      setSentNote(t("fanClaimButtons.weSentASign", { addr }));
      setMode("sent");
    } catch (err: any) {
      if (err?.response?.status === 429) {
        // Three live links already — the fan's link is sitting in their
        // inbox. That's the "sent" outcome, not an error.
        setSentNote(t("fanClaimButtons.aSignInLink"));
        setMode("sent");
      } else {
        setError(t("fanClaimButtons.couldnTSendThe"));
      }
    } finally {
      setBusy(false);
    }
  };

  if (mode === "sent") {
    return (
      <div className="fanclaim-sent">
        <div className="fanclaim-sent-emoji" aria-hidden="true">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#24c961" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        </div>
        <div className="fanclaim-sent-title">{t("fanClaimButtons.checkYourEmail")}</div>
        <div className="fanclaim-sent-sub">{sentNote}</div>
        <button
          type="button"
          className="fanclaim-again"
          onClick={() => setMode("email")}
        >
          {t("fanClaimButtons.useADifferentEmail")}
        </button>
      </div>
    );
  }

  return (
    <div className={`fanclaim${compact ? " fanclaim--compact" : ""}${busy ? " fanclaim--busy" : ""}`}>
      {/* None inside the Android app: it could never finish there, and its
          busy state greyed out Google too (lib/appleSignIn). */}
      {appleSignInAvailable() && (
        <button type="button" className="fanclaim-btn fanclaim-apple" onClick={handleApple} disabled={busy}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.51-3.23 0-1.44.64-2.2.45-3.06-.4C3.79 16.17 4.36 9.02 8.82 8.78c1.15.06 1.95.65 2.63.68.99-.2 1.95-.78 3.01-.7 1.28.1 2.24.61 2.87 1.56-2.64 1.57-2.01 5.02.36 5.98-.49 1.3-1.13 2.58-2.64 3.98zM12.03 8.7c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
          {t("fanClaimButtons.continueWithApple")}
        </button>
      )}

      <div className="fanclaim-google">
        {/* logoAlignment center: the G sits beside its label like the
            Apple/email icons above and below it (founder 8/25). */}
        <GoogleButton onSuccess={handleGoogle} text="continue_with" logoAlignment="center" />
      </div>

      {mode === "email" ? (
        <div className="fanclaim-email-row">
          <input
            className="fanclaim-email-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={t("fanClaimButtons.youEmailCom")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") sendLink();
            }}
            autoFocus
          />
          <button
            type="button"
            className="fanclaim-btn fanclaim-send"
            onClick={sendLink}
            disabled={busy || !EMAIL_RE.test(email.trim())}
          >
            {t("fanClaimButtons.emailMeASign")}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="fanclaim-btn fanclaim-email"
          onClick={() => setMode("email")}
          disabled={busy}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
          {t("fanClaimButtons.continueWithEmail")}
        </button>
      )}

      {error && <div className="fanclaim-error">{error}</div>}
    </div>
  );
};

export default FanClaimButtons;
